import Bluebird from "bluebird";
import uuid from "uuid";

import {
  getPercentScore,
  getStableClassroomStudentId,
  getStableExamAttemptId,
  getTagFromResource,
  SYSTEM_KEY__UPDATE_EXAM_ITEM_STATS,
  SystemTag,
} from "@examspark/common";
import { CreateExamSessionFromLtiSchema } from "@examspark/common-schemas";
import { generateExamSessionId } from "@examspark/exam";
import {
  ExamAttempt,
  ExamScore,
  ExamSession,
  ExamSessionData,
  ExamSessionStatus,
  IDSK,
  LTILaunchExamParams,
  LTILaunchParameters,
  ParsedExamAttempt,
  Resource,
  Tag,
  TagInput,
  Workspace,
} from "@examspark/types";

import { schemaParams, timestampParams } from "..";

import {
  AmplifyEnvironment,
  deleteItem,
  getClassroomStudent,
  getExamConfig,
  getItem,
  parseResourceIdField,
  putClassroomStudentItem,
  putItem,
  queryAllResourcesById,
  queryPaginated,
} from ".";

/**
 * HARD Deletes an exam-session and all of its child records.
 */
export const deleteExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  await resetExamSession(env, examSessionId);

  console.log("Deleting exam session", examSessionId);
  await deleteItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("Resource"),
    {
      id: examSessionId,
      sk: "exam-session",
    },
  );
};

export const getExamSessionResponses = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  return queryAllResourcesById<Resource>(env.getDynamoDBDocumentClient(), {
    tableName: env.getTableName("Resource"),
    id: examSessionId,
    sortKeyBeginsWith: "exam-response:",
  });
};

export const getExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  const result = (
    await getItem<ExamSession>(
      env.getDynamoDBDocumentClient(),
      env.getTableName("resource"),
      {
        id: examSessionId,
        sk: "exam-session",
      },
    )
  ).Item;

  return result;
};

export const putExamSessionResponsesAndRequestScoring = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  responses: unknown,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  await ddb.update({
    TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression: `SET maturity = :status, examSessionResponses = :responses`,
    ExpressionAttributeValues: {
      ":status": ExamSessionStatus.CompleteNeedsScoring,
      ":responses": responses,
    },
  });
};

export const putExamAttemptResponsesAndScores = async (
  env: AmplifyEnvironment,
  examAttempt: IDSK,
  responses: unknown,
  scores: unknown,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  await ddb.update({
    TableName,
    Key: {
      id: examAttempt.id,
      sk: examAttempt.sk,
    },
    UpdateExpression: `SET examSessionResponses = :responses, examSessionResults = :scores, isSubmitted = :submitted`,
    ExpressionAttributeValues: {
      ":responses": responses,
      ":scores": scores,
      ":submitted": true,
    },
  });
};

export const putExamSessionResults = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  examSessionResults: ExamScore,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("resource");

  await ddb.update({
    TableName: TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression: `SET maturity = :status, examSessionResults = :examSessionResults, scoreMin = :scoreMin, scoreMax = :scoreMax, scoreActual = :scoreActual`,
    ExpressionAttributeValues: {
      ":status": ExamSessionStatus.Complete,
      ":examSessionResults": examSessionResults,
      ":scoreMax": 100,
      ":scoreMin": 0,
      ":scoreActual":
        examSessionResults.points.actual != null
          ? getPercentScore(
              examSessionResults.points.actual,
              examSessionResults.points.max,
            )
          : null,
    },
  });

  if (examSessionResults.points.actual == null) {
    await ddb.update({
      TableName: TableName,
      Key: {
        id: examSessionId,
        sk: "exam-session",
      },
      UpdateExpression: `REMOVE scoreActual`,
    });
  }
};

export const pushItemToStatsQueue = async (
  env: AmplifyEnvironment,
  itemId: string,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  await ddb.put({
    TableName: env.getTableName("Resource"),
    Item: {
      id: SYSTEM_KEY__UPDATE_EXAM_ITEM_STATS,
      sk: `${itemId}/item#v0`,
      ...timestampParams(),
    },
  });
};

export const putExamSessionResponseItems = async (
  env: AmplifyEnvironment,
  examSession: Resource | ExamSession,
  examSessionScore: ExamScore,
  tags: TagInput[],
) => {
  const currentDateTime = new Date().toISOString();
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  await Promise.all(
    examSessionScore.items.map(async (item) => {
      await ddb.put({
        TableName,
        Item: {
          id: examSession.id,
          sk: `exam-response:${item.id}/item#v0`,
          resourceId: `${item.id}/item#v0`,
          examSessionId: examSession.id,
          examConfigId: examSession.examConfigId,
          workspaceId: examSession.workspaceId,
          createdAt: currentDateTime,
          updatedAt: currentDateTime,
          schemaVersion: "2020-01-01",
          __typename: "Resource",
          scoreMin: item.score.min,
          scoreMax: item.score.max,
          scoreActual: item.score.actual,
          alignmentIds: item.alignmentIds,
          tags,
        },
      });

      await pushItemToStatsQueue(env, item.id);
    }),
  );
};

export const putExamSessionMaturity = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  maturity: string,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  await ddb.update({
    TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression: [`SET maturity = :status`].join(" "),
    ExpressionAttributeValues: {
      ":status": maturity,
    },
  });
};

export const reopenExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  // we need to un-submit the exam attempt as well
  const currentSession = await ddb.get({
    TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
  });

  if (currentSession?.Item?.currentAttemptNumber) {
    const attempt = await getCurrentExamAttempt(
      env,
      examSessionId,
      currentSession.Item.currentAttemptNumber,
    );

    if (attempt) {
      await ddb.update({
        TableName,
        Key: {
          id: attempt.id,
          sk: attempt.sk,
        },
        UpdateExpression: `SET isSubmitted = :submitted`,
        ExpressionAttributeValues: {
          ":submitted": false,
        },
      });
    }
  }

  await ddb.update({
    TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression: [`SET maturity = :status`].join(" "),
    ExpressionAttributeValues: {
      ":status": ExamSessionStatus.InProgress,
    },
  });
};

export const resetExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  const ddb = env.getDynamoDBDocumentClient();
  const TableName = env.getTableName("Resource");

  await ddb.update({
    TableName,
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression: [
      `SET maturity = :status, scoreMin = :scoreMin, scoreMax = :scoreMax, scoreActual = :scoreActual, scoredAttemptNumber = :scoredAttemptNumber, currentAttemptNumber = :currentAttemptNumber, updatedAt = :updatedAt`,
      `REMOVE examSessionResponses, examSessionResults`,
    ].join(" "),
    ExpressionAttributeValues: {
      ":status": ExamSessionStatus.InProgress,
      ":scoreMax": null,
      ":scoreMin": null,
      ":scoreActual": null,
      ":scoredAttemptNumber": null,
      ":currentAttemptNumber": 1,
      ":updatedAt": new Date().toISOString(),
    },
  });

  // delete exam-attempts
  const attempts = await listAllExamAttempts(env, examSessionId);
  await Promise.all(
    attempts.map((attempt) =>
      deleteItem(
        env.getDynamoDBDocumentClient(),
        env.getTableName("resource"),
        {
          id: attempt.id,
          sk: attempt.sk,
        },
      ),
    ),
  );

  await clearResponsesFromExamSession(env, examSessionId);
};

export const clearResponsesFromExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  const responses = await getExamSessionResponses(env, examSessionId);

  await Bluebird.map(
    responses,
    async (response) => {
      const { id, sk } = parseResourceIdField(response);
      console.log(`Deleting exam session exam-response: ${id}/${sk}`);

      const deleteExamResponsePromise = env.getDynamoDBDocumentClient().delete({
        TableName: env.getTableName("Resource"),
        Key: {
          id: response.id,
          sk: response.sk,
        },
      });

      const itemKey = parseResourceIdField(response);
      const pushItemToStatsQueuePromise = pushItemToStatsQueue(env, itemKey.id);

      await Promise.all([
        deleteExamResponsePromise,
        pushItemToStatsQueuePromise,
      ]);
    },
    { concurrency: 5 },
  );
};

export const createExamSessionFromLtiLaunch = async (
  env: AmplifyEnvironment,
  params: {
    examConfigId: string;
    req: LTILaunchParameters & LTILaunchExamParams;
    workspace: Workspace;
  },
) => {
  const { workspace, req } = params;
  const parsedReq = CreateExamSessionFromLtiSchema.parse(params.req);
  const examConfigId = params.examConfigId;
  const examConfig = await getExamConfig(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    params.examConfigId,
  );
  if (!examConfig) throw new Error("Exam config not found");

  const sourcedIdOrUserId = req.lis_person_sourcedid || req.user_id;
  if (!sourcedIdOrUserId) {
    throw new Error("Missing sourcedId or userId for LTI user login.");
  }

  const currentDateTime = new Date().toISOString();
  const classroomId = getTagFromResource(examConfig, "cohort-classroom")?.value;

  /**
   * Classroom Student:
   *
   * If the student is in a classroom, we need to create a student record so
   * we know about them in the future and can create reports against the
   * student.
   */
  if (classroomId) {
    // IMPORTANT: stable student id (uuidv5) tries to use roster ID (usually
    // simpler) then falls back to opaque LTI ID. For SDK implemenations,
    // `user_id` will likely always be the value used, as `sourcedid` is not
    // likely to be used.
    const userId = req.lis_person_sourcedid || req.user_id;
    if (!userId) throw new Error("Missing user_id for LTI user login.");
    const stableStudentId = getStableClassroomStudentId(classroomId, userId);

    // if student doesn't exist in classroom, create them
    const student = await getClassroomStudent(
      env,
      classroomId,
      stableStudentId,
    );

    if (!student) {
      await putClassroomStudentItem(env, classroomId, {
        stableStudentId,
        idProviderUserId: parsedReq.user_id,
        studentId: sourcedIdOrUserId,
        firstName: parsedReq.lis_person_name_given,
        lastName: parsedReq.lis_person_name_family,
        fullName:
          parsedReq.lis_person_name_full ??
          `${parsedReq.lis_person_name_given} ${parsedReq.lis_person_name_family}`,
        photoURL: parsedReq?.user_image,
        workspaceId: workspace.id,
      });
    }
  }

  if (!req.user_id) throw new Error("Missing user_id for LTI user login.");

  const examSessionId = generateExamSessionId({
    courseId: req.context_id,
    assignmentId: req.resource_link_id,
    examConfigId,
    userId: req.user_id,
  });

  const existingSession = (await getExamSession(env, examSessionId)) as
    | ExamSession
    | undefined;

  // add a separate record at the config level to look up session later
  // for scoring
  const examConfigSessionItem = {
    id: examConfigId,
    sk: `exam-config-session#${examSessionId}`,
    workspaceId: workspace.id,
    examId: examConfig.resourceId,
    examConfigId,
    resourceId: [examSessionId, "exam-session"].join("/"),
    ...timestampParams(),
    ...schemaParams(),
    __typename: "ExamConfigSession",
  };

  // console.log("creating config-session");
  await putItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    examConfigSessionItem,
  );
  const tags = formatTagsForExam(req, examConfig, workspace);

  if (existingSession) {
    return existingSession;
  }

  const examSessionKey = uuid.v4();
  const examSessionItem: ExamSession = {
    id: examSessionId,
    sk: "exam-session",
    schemaVersion: "2020-01-01",
    workspaceId: workspace.id,
    __typename: "ExamSession",
    name: parsedReq.lis_person_name_full,
    examId: examConfig.resourceId,
    examConfigId,
    currentAttemptNumber: 1,
    maturity: "IN_PROGRESS",
    tags,
    examSessionData: {
      accessKey: examSessionKey,
      examConfigId: params.examConfigId,
      studentId: parsedReq.user_id || "00000",
      lti: {
        assignmentId: parsedReq.custom_canvas_assignment_id,
        consumerKey: parsedReq.oauth_consumer_key,
        contextId: parsedReq.context_id,
        contextLabel: parsedReq.context_label,
        contextTitle: parsedReq.context_title,
        courseId: parsedReq.custom_canvas_course_id,
        domain: parsedReq.custom_canvas_api_domain,
        outcomeUrl: parsedReq.lis_outcome_service_url,
        sourcedId: parsedReq.lis_result_sourcedid,
        toolConsumerGuid: parsedReq.tool_consumer_instance_guid,
        toolConsumerName: parsedReq.tool_consumer_instance_name,
        toolConsumerProductCode:
          parsedReq.tool_consumer_info_product_family_code,
        toolConsumerVersion: parsedReq.tool_consumer_info_version,
        uniqueUserId: parsedReq.user_id,
        userId: parsedReq.user_id,

        // ags properties
        agsAllowedScopes: parsedReq.ags_allowed_scopes,
        agsLineItemUrl: parsedReq.ags_line_item_url,
        agsLineItemsUrl: parsedReq.ags_line_items_url,
        toolClientId: parsedReq.tool_client_id,
        toolIssuer: parsedReq.tool_issuer,
      },
    } satisfies ExamSessionData,
    createdAt: currentDateTime,
    updatedAt: currentDateTime,
  };

  if (parsedReq.user_image) {
    examSessionItem.imageUrl = parsedReq.user_image;
  }

  // console.log("creating session", examSessionId);
  await putItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    examSessionItem,
  );

  return examSessionItem;
};

export const formatTagsForExam = (
  req: any,
  examConfig: Resource,
  workspace: Workspace,
) => {
  const tags: Tag[] = [
    {
      key: SystemTag.CohortCourseId,
      type: "string",
      value: req.context_id,
    },
    {
      key: "cohort-course-title",
      type: "string",
      value: req.context_title,
    },
    {
      key: "cohort-course-label",
      type: "string",
      value: req.context_label,
    },
    {
      key: "exam-resource-link-id",
      type: "string",
      value: req.resource_link_id,
    },
    {
      key: "exam-resource-link-title",
      type: "string",
      value: req.resource_link_title,
    },
    {
      key: "exam-resource-link-description",
      type: "string",
      value: req.resource_link_description,
    },
    // {
    //   key: "exam-launch-user-id",
    //   type: "string",
    //   value: req.body.user_id,
    // },
  ];

  const cohortSchoolTag = getTagFromResource(examConfig, "cohort-school");
  if (cohortSchoolTag != null) {
    tags.push(cohortSchoolTag);
  }

  const requiredTags = [
    "cohort-country",
    "cohort-state",
    "cohort-state-region",
    "cohort-state-district",
  ];

  for (const key of requiredTags) {
    if (!workspace.tags?.find((t: any) => t.key === key)) {
      console.log("missing", key);
      throw new Error(
        `Invalid workspace configuration for ${workspace.name} (${workspace.id})`,
      );
    }

    const found = workspace.tags?.find((t) => t?.key === key);
    if (found) tags.push(found);
  }

  return tags;
};

export const getCurrentExamAttempt = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  currentAttemptNumber: number,
) => {
  const result = await getItem<ExamAttempt>(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    {
      id: getStableExamAttemptId(examSessionId, currentAttemptNumber),
      sk: `${examSessionId}/exam-session#exam-attempt`,
    },
  );

  return result.Item;
};

export const listAllExamAttempts = async (
  env: AmplifyEnvironment,
  examSessionId: string,
) => {
  const paginator = queryPaginated<ParsedExamAttempt>({
    ddbClient: env.getDynamoDBDocumentClient(),
    query: {
      TableName: env.getTableName("resource"),
      IndexName: "bySortKey",
      KeyConditionExpression: "sk = :sk",
      ExpressionAttributeValues: {
        ":sk": `${examSessionId}/exam-session#exam-attempt`,
      },
    },
  });

  // consume the generator and return an array
  const attempts: ParsedExamAttempt[] = [];
  for await (const attempt of paginator) {
    attempts.push(attempt);
  }

  return attempts;
};

export const createNextExamAttempt = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  nextAttemptNumber: number,
  data: Omit<ExamAttempt, keyof IDSK>,
) => {
  const result = await env.getDynamoDBDocumentClient().put({
    TableName: env.getTableName("resource"),
    Item: {
      ...data,

      id: getStableExamAttemptId(examSessionId, nextAttemptNumber),
      sk: `${examSessionId}/exam-session#exam-attempt`,
      __typename: "ExamAttempt",

      ...schemaParams(),
      ...timestampParams(),
    },
  });

  return result;
};

export const putScoredExamSession = async (
  env: AmplifyEnvironment,
  examSessionId: string,
  data: Pick<
    ExamSession,
    | "examSessionResponses"
    | "examSessionResults"
    | "scoredAttemptNumber"
    | "scoreActual"
  >,
) => {
  const result = await env.getDynamoDBDocumentClient().update({
    TableName: env.getTableName("resource"),
    Key: {
      id: examSessionId,
      sk: "exam-session",
    },
    UpdateExpression:
      "SET " +
      [
        "examSessionResponses = :examSessionResponses",
        "examSessionResults = :examSessionResults",
        "scoredAttemptNumber = :scoredAttemptNumber",

        "maturity = :maturity",
        "updatedAt = :updatedAt",

        "scoreActual = :scoreActual",
        "scoreMin = :scoreMin",
        "scoreMax = :scoreMax",
      ].join(", "),
    ExpressionAttributeValues: {
      ":examSessionResponses": data.examSessionResponses,
      ":examSessionResults": data.examSessionResults,
      ":scoredAttemptNumber": data.scoredAttemptNumber,

      ":maturity": ExamSessionStatus.Complete,
      ":updatedAt": new Date().toISOString(),

      ":scoreMin": 0,
      ":scoreMax": 100,
      ":scoreActual": data.scoreActual ?? null,
    },
  });

  return result;
};
